import { useEffect, useId, useMemo, useRef, useState } from "react";
import { PASSWORD_MINIMUM_LENGTH } from "@verity-connect/domain";
import { BrandLockup } from "./BrandLockup";
import { TurnstileField, turnstileToken } from "./TurnstileField";
import { associationPresentation } from "./association-presentation";
import { passkeysAvailable } from "./usePasskeys";
import type { JoinEntryDecision } from "./JoinRoute";

export type JoinAudience = "attendee" | "staff" | "presenter" | "school" | "exhibitor";

export interface JoinSubmission {
  audience: JoinAudience;
  displayName: string;
  schoolName: string | null;
  email: string;
  password: string;
  turnstileToken: string;
}

export interface JoinPageProps {
  audience: JoinAudience;
  schools: readonly { id: string; name: string }[];
  onResolveEmail: (email: string, turnstileToken: string) => Promise<JoinEntryDecision>;
  onSubmit: (submission: JoinSubmission) => Promise<void>;
  onUsePasskey: (() => Promise<void>) | null;
  completed: boolean;
  onAddDevice: () => Promise<void>;
  onSkipDevice: () => void;
  busy?: boolean;
  error?: string | null;
  onClearError?: () => void;
}

const NOT_LISTED = "__not_listed__";
const DOOR_NAME: Record<JoinAudience, string> = {
  attendee: "convention",
  staff: "staff",
  presenter: "presenter",
  school: "school",
  exhibitor: "exhibitor",
};

export function JoinPage({
  audience,
  schools,
  onResolveEmail,
  onSubmit,
  onUsePasskey,
  completed,
  onAddDevice,
  onSkipDevice,
  busy = false,
  error = null,
  onClearError,
}: JoinPageProps) {
  const presentation = associationPresentation();
  const emailId = useId();
  const nameId = useId();
  const schoolId = useId();
  const passwordId = useId();
  const passwordHintId = useId();
  const [email, setEmail] = useState("");
  const [decision, setDecision] = useState<JoinEntryDecision | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [schoolChoice, setSchoolChoice] = useState("");
  const [otherSchool, setOtherSchool] = useState("");
  const [password, setPassword] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [touched, setTouched] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const errorRef = useRef<HTMLParagraphElement>(null);

  const shownError = localError ?? error;
  useEffect(() => { if (shownError) errorRef.current?.focus(); }, [shownError]);
  useEffect(() => { if (decision) headingRef.current?.focus(); }, [decision]);

  const schoolName = schoolChoice === NOT_LISTED
    ? otherSchool.trim() || null
    : schools.find((school) => school.id === schoolChoice)?.name ?? null;
  const registered = decision?.kind === "registered" ? decision : null;
  const needsProfile = audience === "attendee" && decision?.kind === "new";
  const effectiveName = registered?.displayName ?? displayName.trim();
  const effectiveSchool = registered?.schoolName ?? schoolName;
  const passwordShort = password.length > 0 && password.length < PASSWORD_MINIMUM_LENGTH;
  const complete = useMemo(
    () => Boolean(decision && password.length >= PASSWORD_MINIMUM_LENGTH && (!needsProfile || (effectiveName && effectiveSchool))),
    [decision, password, needsProfile, effectiveName, effectiveSchool],
  );

  function startOver() {
    setDecision(null);
    setPassword("");
    setTouched(false);
    setLocalError(null);
    onClearError?.();
    requestAnimationFrame(() => document.getElementById(emailId)?.focus());
  }

  const heading = completed ? "You're signed in"
    : !decision ? `Continue to ${presentation.appName}`
    : decision.kind === "password" ? "Welcome back"
      : decision.kind === "passkey" ? "Welcome back"
      : decision.kind === "registered" ? "Create your sign-in"
        : decision.kind === "invited" ? `Create your ${DOOR_NAME[audience]} sign-in`
          : "Create your account";
  const standfirst = completed ? "Would you like this device to remember you?"
    : !decision ? "Enter your email to continue."
    : decision.kind === "password" ? `Enter your password to open your ${DOOR_NAME[audience]} account.`
      : decision.kind === "passkey" ? "Sign in with this device, the way you set it up."
      : decision.kind === "registered" ? `Your registration for ${decision.eventName} is ready. Choose a password to finish.`
        : decision.kind === "invited" ? "WVCEA invited this address. Choose the password you will use from now on."
          : "Tell us who you are, then choose a password.";

  return (
    <div className="page join-page">
      <div className="join-card">
        <BrandLockup className="brand-lockup-card" />
        <p className="join-kicker">{decision?.kind === "registered" ? "Registration found" : decision?.kind === "password" ? "Sign in" : "Welcome"}</p>
        <h1 className="join-heading" data-page-heading tabIndex={-1} ref={headingRef}>{heading}</h1>
        <p className="join-standfirst">{standfirst}</p>
        {shownError && <p className="form-error" role="alert" tabIndex={-1} ref={errorRef}>{shownError}</p>}

        {completed ? (
          <div className="join-form">
            <div className="join-device-offer">
              <strong>Make next time faster</strong>
              <p>Use Face ID, Touch ID, a fingerprint, or your device screen lock instead of typing your password.</p>
            </div>
            <button className="primary-action join-submit" type="button" disabled={busy} onClick={() => void onAddDevice()}>{busy ? "Opening device sign-in…" : "Set up device sign-in"}</button>
            <button className="sign-in-alternative" type="button" disabled={busy} onClick={onSkipDevice}>Not now</button>
          </div>
        ) : !decision ? (
          <>
            <form className="join-form" onSubmit={(event) => {
              event.preventDefault();
              setTouched(true);
              setLocalError(null);
              onClearError?.();
              if (!email.trim() || busy) return;
              void onResolveEmail(email.trim(), turnstileToken(event.currentTarget))
                .then((next) => { setEmail(next.email); setDecision(next); setTouched(false); })
                .catch((caught) => setLocalError(caught instanceof Error ? caught.message : "That email could not be checked."));
            }}>
              <label className="join-field" htmlFor={emailId}>
                <span>Email</span>
                <input id={emailId} name="email" type="email" inputMode="email" autoComplete="username webauthn" autoCapitalize="none" spellCheck={false} enterKeyHint="go" autoFocus required value={email} onChange={(event) => setEmail(event.target.value)} aria-invalid={touched && !email.trim() ? true : undefined} />
                {touched && !email.trim() && <em className="join-note">Enter your email address.</em>}
              </label>
              <TurnstileField />
              <button className="primary-action join-submit" type="submit" disabled={busy}>{busy ? "Checking…" : "Continue"}</button>
            </form>
            {onUsePasskey && passkeysAvailable() && (
              <button className="sign-in-alternative" type="button" disabled={busy} onClick={() => void onUsePasskey()}>
                Use Face ID, Touch ID, fingerprint, or device sign-in
              </button>
            )}
          </>
        ) : decision.kind === "invited" ? (
          <>
          <form className="join-form" onSubmit={(event) => {
            event.preventDefault();
            setTouched(true);
            if (busy || password.length < PASSWORD_MINIMUM_LENGTH) return;
            void onSubmit({ audience, displayName: effectiveName, schoolName: null, email, password, turnstileToken: turnstileToken(event.currentTarget) });
          }}>
            <p className="join-note"><strong>{email}</strong> · <button className="text-action" type="button" onClick={startOver}>Use a different email</button></p>
            <label className="join-field" htmlFor={passwordId}>
              <span>Choose a password</span>
              <span className="join-password">
                <input id={passwordId} name="password" type={revealed ? "text" : "password"} autoComplete="new-password" required minLength={PASSWORD_MINIMUM_LENGTH} autoFocus value={password} onChange={(event) => setPassword(event.target.value)} />
                <button className="join-reveal" type="button" aria-pressed={revealed} onClick={() => setRevealed((value) => !value)}>{revealed ? "Hide" : "Show"}</button>
              </span>
              <em className="join-note">At least {PASSWORD_MINIMUM_LENGTH} characters. You will use this and your email address from now on.</em>
            </label>
            <TurnstileField />
            <button className="primary-action join-submit" type="submit" disabled={busy}>{busy ? "Opening…" : "Create sign-in"}</button>
          </form>
          {decision.hasPasskey && onUsePasskey && passkeysAvailable() && (
            <button className="sign-in-alternative" type="button" disabled={busy} onClick={() => void onUsePasskey()}>
              Or use Face ID, Touch ID, fingerprint, or device sign-in
            </button>
          )}
          </>
        ) : (
          <form className="join-form" onSubmit={(event) => {
            event.preventDefault();
            setTouched(true);
            if (!complete || busy) return;
            void onSubmit({ audience, displayName: effectiveName, schoolName: effectiveSchool, email, password, turnstileToken: turnstileToken(event.currentTarget) });
          }}>
            <p className="join-note"><strong>{email}</strong> · <button className="text-action" type="button" onClick={startOver}>Use a different email</button></p>
            {registered?.schoolName && <p className="join-note">Registered through {registered.schoolName}.</p>}

            {needsProfile && (
              <>
                <label className="join-field" htmlFor={nameId}>
                  <span>Your name</span>
                  <input id={nameId} name="name" type="text" autoComplete="name" required value={displayName} onChange={(event) => setDisplayName(event.target.value)} aria-invalid={touched && !effectiveName ? true : undefined} />
                  {touched && !effectiveName && <em className="join-note">Enter your name.</em>}
                </label>
                <label className="join-field" htmlFor={schoolId}>
                  <span>Your school</span>
                  <select id={schoolId} name="school" required value={schoolChoice} onChange={(event) => setSchoolChoice(event.target.value)} aria-invalid={touched && !effectiveSchool ? true : undefined}>
                    <option value="">Choose your school</option>
                    {schools.map((school) => <option key={school.id} value={school.id}>{school.name}</option>)}
                    <option value={NOT_LISTED}>My school is not listed</option>
                  </select>
                  {schoolChoice === NOT_LISTED && <input className="join-other-school" name="otherSchool" type="text" autoComplete="organization" placeholder="Type your school's name" aria-label="Your school's name" required value={otherSchool} onChange={(event) => setOtherSchool(event.target.value)} />}
                  {touched && !effectiveSchool && <em className="join-note">Choose your school or type its name.</em>}
                </label>
              </>
            )}

            <label className="join-field" htmlFor={passwordId}>
              <span>{decision.kind === "password" ? "Password" : "Choose a password"}</span>
              <span className="join-password">
                <input id={passwordId} name="password" type={revealed ? "text" : "password"} autoComplete={decision.kind === "password" ? "current-password" : "new-password"} required minLength={PASSWORD_MINIMUM_LENGTH} aria-describedby={passwordHintId} aria-invalid={touched && passwordShort ? true : undefined} value={password} onChange={(event) => setPassword(event.target.value)} autoFocus />
                <button className="join-reveal" type="button" aria-pressed={revealed} onClick={() => setRevealed((value) => !value)}>{revealed ? "Hide" : "Show"}</button>
              </span>
              <em className="join-note" id={passwordHintId}>{decision.kind === "password" ? "Use the password you chose for this account." : `At least ${PASSWORD_MINIMUM_LENGTH} characters.`}</em>
            </label>
            <TurnstileField />
            <button className="primary-action join-submit" type="submit" disabled={busy}>{busy ? "Opening…" : decision.kind === "password" ? "Sign in" : "Continue"}</button>
          </form>
        )}
      </div>
    </div>
  );
}
